import { RefObject, useEffect } from 'react';

const DRAG_THRESHOLD = 4;

export function useDragScroll(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let startX = 0;
    let startScrollLeft = 0;
    let isDown = false;
    let isDragging = false;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return; // left button only
      isDown = true;
      isDragging = false;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDown) return;
      const delta = e.clientX - startX;
      // Threshold prevents treating small cursor jitter during a click as a drag
      if (!isDragging && Math.abs(delta) > DRAG_THRESHOLD) {
        isDragging = true;
      }
      if (isDragging) {
        el.scrollLeft = startScrollLeft - delta;
      }
    };

    const onPointerUp = () => {
      isDown = false;
      el.style.cursor = '';
    };

    // Capture phase lets us swallow the click before it reaches Tabs.Item,
    // preventing an accidental tab switch at the end of a drag gesture.
    const onClickCapture = (e: MouseEvent) => {
      if (isDragging) {
        e.stopPropagation();
        isDragging = false;
      }
    };

    el.addEventListener('pointerdown', onPointerDown);
    // pointermove/pointerup on window so dragging outside the element still works
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    el.addEventListener('click', onClickCapture, true);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, [ref]);
}
